import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDatepicker } from '@angular/material/datepicker';

import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, finalize } from 'rxjs/operators';
import { Moment } from 'moment';

import { multiEmailValidator } from '../../../cp/utils/validators';

import { CommonApiService } from '../../../shared/services/common-api.service';
import { SnackBarService } from '../../../snack-bar.service';

import {
  BukTicketDefaultExpiryDates,
  BukTicketDefaultLanguages,
  BukTicketDefaultPeriods,
  BukTicketSenderInfo,
} from '../../models/buk-ticket.model';

export function getDefaultEmailTitle(
  lang: 'ko' | 'en',
  senderName: string
): string {
  return lang === 'ko'
    ? `[북티켓 선물] ${senderName} 님이 보내신 북티켓이 도착했습니다.`
    : `[Book Ticket Gift] The book ticket from ${senderName} has arrived.`;
}

export function getDefaultEmailMessage(lang: 'ko' | 'en'): string {
  return lang === 'ko'
    ? '💌 북티켓을 선물로 드립니다.'
    : 'We send you a free e-book reading ticket. ';
}

const EMAIL_REGEX =
  /(([\w-]+(?:(?:\.|\+)[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?))/gi;

@Component({
  selector: 'app-email-ticket-form',
  templateUrl: './email-ticket-form.component.html',
  styleUrls: ['./email-ticket-form.component.scss'],
})
export class EmailTicketFormComponent implements OnInit {
  @Input() set senderInfo(senderInfo: BukTicketSenderInfo) {
    this._senderInfo = senderInfo;
    this.updateSenderInfo(senderInfo);

    if (senderInfo) {
      this.updateFormDefaultValue();
    }
  }
  @Input() set isUnlimitedTicket(isUnlimitedTicket: boolean) {
    this._isUnlimitedTicket = isUnlimitedTicket;
    this.updateTicketFormGroup(isUnlimitedTicket);
  }
  public _isUnlimitedTicket: boolean;

  @Input() gaTrackingId: string;

  @Output() focusChange = new EventEmitter<string | undefined>();
  @Output() validityChange = new EventEmitter<boolean>();
  @Output() formChange = new EventEmitter<void>();

  @ViewChild('picker') picker: MatDatepicker<Date>;

  public languages = BukTicketDefaultLanguages;
  public periods = BukTicketDefaultPeriods;
  public expiryDates = BukTicketDefaultExpiryDates;
  public messageMaxlength = 1000;

  public ticketFormGroup: FormGroup;
  public _senderInfo: BukTicketSenderInfo;

  public isDatePickerMode = false;
  public minDate = new Date();
  public maxDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1)
  );

  public logoImageUploadSubscription: Subscription;
  public imageName: string;

  public isEmailCollapsed = false;
  public emails = [];
  public emailError: boolean;
  public readonly _separatorKeysCodes = [ENTER, COMMA, SEMICOLON, SPACE];
  public emailInputChange$: Subject<any> = new Subject<any>(); // any => InputEvent

  get defaultEmailTitle(): string {
    return getDefaultEmailTitle(
      this.ticketFormGroup.get('language').value,
      this._senderInfo?.name
    );
  }

  get defaultEmailMessage(): string {
    return getDefaultEmailMessage(this.ticketFormGroup.get('language').value);
  }

  constructor(
    private formBuilder: FormBuilder,
    private commonApiService: CommonApiService,
    private snackBarService: SnackBarService,
    private commonAPIService: CommonApiService
  ) {
    this.ticketFormGroup = this.formBuilder.group({
      language: ['ko', Validators.required],
      emailTitle: [''],
      sender_name: [''],
      sender_email: ['', [multiEmailValidator]],
      sender_logo_image_url: [''],
      save_sender: [{ value: false, disabled: true }],
      email: ['', [Validators.required, multiEmailValidator]],
      message: [''],
    });

    this.ticketFormGroup.get('save_sender').disable();
  }

  ngOnInit(): void {
    this.emailInputChange$
      .pipe(
        debounceTime(300),
        filter(() => this.emails.length === 0)
      )
      .subscribe((inputEvent) => {
        this.setEmailControlValue(inputEvent.srcElement.value);
      });

    this.ticketFormGroup.valueChanges.subscribe(() => {
      this.activateSaveSenderCheckbox();

      this.validityChange.emit(!this.ticketFormGroup.invalid);
      this.formChange.emit();
    });
  }

  activateSaveSenderCheckbox(): void {
    if (
      this.ticketFormGroup.get('save_sender').disabled &&
      (this.ticketFormGroup.get('sender_name').dirty ||
        this.ticketFormGroup.get('sender_email').dirty)
    ) {
      this.ticketFormGroup.get('save_sender').enable();
    }
  }

  updateTicketFormGroup(isUnlimitedTicket: boolean): void {
    if (isUnlimitedTicket) {
      this.ticketFormGroup.removeControl('expiryDate');
      this.ticketFormGroup.removeControl('period');
    } else {
      this.ticketFormGroup.addControl(
        'expiryDate',
        new FormControl('1m', Validators.required)
      );
      this.ticketFormGroup.addControl(
        'period',
        new FormControl('1m', Validators.required)
      );
    }
  }

  updateSenderInfo(senderInfo: BukTicketSenderInfo): void {
    this.ticketFormGroup.patchValue({
      sender_name: senderInfo.name,
      sender_email: senderInfo.email,
      sender_logo_image_url: senderInfo.logo_image_url,
    });
  }

  updateFormDefaultValue(): void {
    if (this.ticketFormGroup.get('emailTitle').pristine) {
      this.ticketFormGroup.patchValue({
        emailTitle: this.defaultEmailTitle,
      });
    }

    if (this.ticketFormGroup.get('message').pristine) {
      this.ticketFormGroup.patchValue({
        message: this.defaultEmailMessage,
      });
    }
  }

  logoImageChanged(image: any): void {
    if (this.logoImageUploadSubscription) {
      this.logoImageUploadSubscription.unsubscribe(); // cancel previous request
      this.logoImageUploadSubscription = undefined;
    }

    if (!image) {
      this.ticketFormGroup.patchValue({ sender_logo_image_url: null });
      this.ticketFormGroup.patchValue({ save_sender: null });
      this.ticketFormGroup.get('save_sender').disable();
      this.imageName = null;
    } else {
      this.imageName = image.name;
      this.logoImageUploadSubscription = this.commonApiService
        .uploadImage(image)
        .pipe(finalize(() => (this.logoImageUploadSubscription = undefined)))
        .subscribe(
          (response) => {
            this.ticketFormGroup.patchValue({
              sender_logo_image_url: response[0].url,
            });

            this.ticketFormGroup.get('save_sender').enable();
          },
          (error: HttpErrorResponse) => {
            this.snackBarService.openConfirm({
              key: 'common.messages.imageUploadErrorWithCode',
              param: { code: error.status },
            });
          }
        );
    }
  }

  onEmailChipRemoved(email: string): void {
    const index = this.emails.indexOf(email);

    if (index >= 0) {
      this.emails.splice(index, 1);
      this.ticketFormGroup.patchValue({ email: this.emails.join(',') });
      this.ticketFormGroup.controls.email.markAsDirty();
    }
  }

  collapseEmailChipsIfNeeded(): void {
    this.isEmailCollapsed = this.emails.length >= 10;
  }

  onEmailChipClick(): void {
    this.isEmailCollapsed = false;
  }

  onEmailInputFocus(): void {
    this.isEmailCollapsed = false;
  }

  onEmailInputBlur(): void {
    this.collapseEmailChipsIfNeeded();
  }

  onEmailChipInputTokenEnd(event: MatChipInputEvent): void {
    const input = event.input;

    this.addEmail(event.value);

    if (input) {
      input.value = '';
    }
  }

  onEmailPaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const text = (event.clipboardData || (window as any).clipboardData).getData(
      'text'
    );

    const matches = text.matchAll(EMAIL_REGEX);
    for (const m of matches) {
      this.addEmail(m[1]);
    }
  }

  addEmail(value: string): void {
    value = (value || '').trim();

    if (!value) {
      return;
    }

    if (
      /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        value
      )
    ) {
      this.emails.push(value);
      this.setEmailControlValue(this.emails.join(','));
      this.emailError = false;
    } else {
      this.emailError = true;
    }
  }

  setEmailControlValue(value: string): void {
    this.ticketFormGroup.patchValue({ email: value });
    this.ticketFormGroup.controls.email.markAsDirty();
  }

  onFileChange(files: any): void {
    this.commonAPIService
      .extractEmailFromFile(files)
      .subscribe((extractedEmails) => {
        extractedEmails.forEach((email) => this.addEmail(email));
        this.collapseEmailChipsIfNeeded();
      });
  }

  openDatePicker(): void {
    this.picker.open();
    this.isDatePickerMode = true;
  }

  updateExpiryDate(event: Moment): void {
    this.ticketFormGroup.patchValue({
      expiryDate: event,
    });
  }

  resetTicketFormGroup(): void {
    const _resetValue: any = {
      language: 'ko',
      emailTitle: getDefaultEmailTitle('ko', this._senderInfo.name),
      sender_name: this._senderInfo.name,
      sender_email: this._senderInfo.email,
      sender_logo_image_url: this._senderInfo.logo_image_url,
      save_sender: false,
      email: '',
      message: getDefaultEmailMessage('ko'),
    };

    if (!this._isUnlimitedTicket) {
      _resetValue.period = '1m';
      _resetValue.expiryDate = '1m';

      this.isDatePickerMode = false;
    }

    this.ticketFormGroup.reset(_resetValue);

    this.ticketFormGroup.get('save_sender').disable();
    this.emails = [];
    this.imageName = null;
  }

  resetEmails(): void {
    this.emails = [];
    this.ticketFormGroup.controls.email.setValue('');
  }
}
